import React, { Fragment, useRef, useState } from "react";

import { Menu, Transition } from '@headlessui/react';
import apiService from "../services/ApiService";
import snakeToPascal  from "../tools";
import FieldsInitializer from "./ModelDataFields/Input/Initializer";
import { useEditorStore } from "../shared/stores/useEditorStore";
import { mapModelData } from "./Draggables/utils/treeUtils";
import { useScenarioTree } from "../shared/hooks/queries/useScenarioTree";
import LibraryBrowserModal from "./LibraryBrowserModal";
import { useChatOpen, useSharedChat } from "../hooks/useAgentContext";
import { cn } from "../lib/utils";


function DocumentationButton({ elem }) {
    const { setIsChatOpen } = useChatOpen();
    const { sendMessage } = useSharedChat();

    const handleDocumentation = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setIsChatOpen(true);
        sendMessage(`Explain me what is the block named '${snakeToPascal(elem.type)}' (type: ${elem.type}) and how to use it ?`);
    };

    return (
        <div>
        {
            elem.documentation ?
                <span
                    role="button"
                    tabIndex={0}
                    title="Documentation"
                    aria-label="open documentation"
                    className="inline-flex items-center justify-center rounded w-5 h-5 ml-1 -mt-0.5 hover:opacity-100 transition-opacity bg-transparent cursor-pointer"
                    onClick={handleDocumentation}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDocumentation(e);
                        }
                    }}
                >
                    <span className="material-symbols-outlined text-[15px]">help_outline</span>
                </span> : null
        }
        </div>
    )
}

// Full typing of item/tree/currentState would clash with the partial mocks
// used by AddButtonMenu.test.tsx, so they stay `any` for now. `allowedTypes`
// is typed to match DraggableDataGroupProps / TreeItemActionsProps.
interface AddButtonMenuProps {
    item: any;
    tree: any;
    reloadState?: () => void;
    bgcolor: string;
    scenario_id: number;
    recreateExcelInputIfSync?: () => void;
    level: number;
    currentState: any;
    allowedTypes?: Set<string> | null;
}

export default function AddButtonMenu({ item, tree, reloadState: _reloadState, bgcolor, scenario_id, recreateExcelInputIfSync, level, currentState, allowedTypes }: AddButtonMenuProps) {
    recreateExcelInputIfSync;

    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const { patchTree } = useScenarioTree(scenarioId);
    const [libraryBrowserOpen, setLibraryBrowserOpen] = useState(false);

    // Merge the items returned by /library/apply into the scenario-tree cache.
    // We can't use RELOAD_STATE_FROM_API here: it invalidates SCENARIO_TREE_KEY,
    // whose queryFn returns an empty initial state, which would wipe the tree.
    const handleLibraryApplied = (NewItems: { input: Record<string, any>; output: Record<string, any> }) => {
        const parentItem = item;
        let newTopItem: any = null;
        Object.values(NewItems['input']).forEach((NewItem: any) => {
            if (NewItem.parent_id == parentItem.id) {
                newTopItem = NewItem;
            }
        });

        patchTree((prev) => {
            const newInputItems = { ...prev.Input.tree.items };
            const newOutputItems = { ...prev.Output.tree.items };

            const buildTreeItem = (NewItem: any, newId: string) => ({
                id: newId,
                name: NewItem.name,
                type: NewItem.type,
                expanded: false,
                position: NewItem.position,
                kind: NewItem.kind,
                has_related_output: NewItem.has_related_output,
                relatedModelDataId: NewItem.related_model_id,
                documentation: NewItem.documentation,
                editing: false,
                anchorEl: null,
                deleteModalOpen: false,
                detailsModalOpen: false,
                childrens: NewItem.childrens.map((e: any) => e.toString()),
                possibleChildrens: NewItem.possible_childrens,
                childrensDefinition: NewItem.childrens_definition,
                parentId: NewItem.parent_id != null ? NewItem.parent_id.toString() : '',
                fields: FieldsInitializer(NewItem.fields),
                isInconsiderable: NewItem.is_inconsiderable,
                isDeletable: NewItem.is_deletable,
                sensitivityModelData: null,
            });

            Object.entries(NewItems['input']).forEach(([newId, NewItem]: any) => {
                newInputItems[newId] = buildTreeItem(NewItem, newId);
            });

            if (newTopItem && newInputItems[parentItem.id]) {
                newInputItems[parentItem.id] = {
                    ...newInputItems[parentItem.id],
                    childrens: [newTopItem.id.toString(), ...newInputItems[parentItem.id].childrens],
                    expanded: true,
                    anchorEl: null,
                };
            }

            Object.entries(NewItems['output']).forEach(([newId, NewItem]: any) => {
                newOutputItems[newId] = buildTreeItem(NewItem, newId);
            });

            if (newTopItem?.has_related_output) {
                const outputParentId = parentItem.relatedModelDataId?.toString();
                if (outputParentId && newOutputItems[outputParentId]) {
                    newOutputItems[outputParentId] = {
                        ...newOutputItems[outputParentId],
                        childrens: [newTopItem.related_model_id.toString(), ...newOutputItems[outputParentId].childrens],
                    };
                }
            }

            return {
                ...prev,
                isComputed: false,
                isSync: false,
                Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
                Output: { ...prev.Output, tree: { ...prev.Output.tree, items: newOutputItems } },
            };
        });

        const nodeKey = parentItem.kind + parentItem.id.toString();
        useEditorStore.getState().setNodeExpanded(nodeKey, true);
    };

    const addItemOnTree = async (parentItem, elem, tree, scenario_id) => {

        // We check that no other items is currently in editing mode
        if (Object.values(tree.items).filter(e => e['editing'] === true).length)
            return

        const nodeKey = parentItem.kind + parentItem.id.toString();

        // Expand the parent and show loading spinner immediately
        useEditorStore.getState().setNodeExpanded(nodeKey, true);
        useEditorStore.getState().setNodeLoading(nodeKey, true);
        patchTree((prev) => ({ ...prev }));

        // Load unloaded siblings first so they appear alongside the new child
        const siblingIds = parentItem.childrens;
        const hasUnloadedSiblings = siblingIds.some(
            (id) => !currentState['Input'].tree.items[id]
        );
        let loadedSiblings: any[] = [];
        if (hasUnloadedSiblings) {
            try {
                const sibResponse = await apiService.getModelDataChildren(parseInt(parentItem.id));
                if (sibResponse?.ok) {
                    loadedSiblings = await sibResponse.json();
                }
            } catch (err) {
                console.error("Failed to load siblings:", err);
            }
        }

        // Create the new model data
        const response = await apiService.postModelData(
            (elem.custom_name) ? '' : snakeToPascal(elem.type),
            elem.type,
            scenario_id,
            parseInt(parentItem.id),
            0,
        );

        if (response) {
            const NewItems = await response.json()

            let newTopItem = null;
            Object.entries(NewItems['input']).forEach(([, NewItem]: any) => {
                if (NewItem.parent_id == parentItem.id) {
                    newTopItem = NewItem;
                }
            });

            // Apply ALL mutations atomically via patchTree so that
            // concurrent WebSocket events cannot create a divergent
            // items object that loses our changes.
            patchTree((prev) => {
                const newInputItems = { ...prev.Input.tree.items };
                const newOutputItems = { ...prev.Output.tree.items };

                // Merge loaded siblings
                for (const child of loadedSiblings) {
                    const childId = child.id.toString();
                    if (!newInputItems[childId]) {
                        newInputItems[childId] = mapModelData(child);
                    }
                }

                // Clear editing flag on all existing items
                Object.keys(newInputItems).forEach((key) => {
                    if (newInputItems[key].editing) {
                        newInputItems[key] = { ...newInputItems[key], editing: false };
                    }
                });

                // Add new input items
                Object.entries(NewItems['input']).forEach(([newId, NewItem]: any) => {
                    newInputItems[newId] = {
                        id: newId,
                        name: NewItem.name,
                        type: NewItem.type,
                        expanded: false,
                        position: NewItem.position,
                        kind: NewItem.kind,
                        has_related_output: NewItem.has_related_output,
                        relatedModelDataId: NewItem.related_model_id,
                        documentation: NewItem.documentation,
                        editing: NewItem.parent_id == parentItem.id ? elem.custom_name : false,
                        anchorEl: null,
                        deleteModalOpen: false,
                        detailsModalOpen: false,
                        childrens: NewItem.childrens.map(e => e.toString()),
                        possibleChildrens: NewItem.possible_childrens,
                        childrensDefinition: NewItem.childrens_definition,
                        parentId: NewItem.parent_id.toString(),
                        fields: FieldsInitializer(NewItem.fields),
                        isInconsiderable: NewItem.is_inconsiderable,
                        isDeletable: NewItem.is_deletable,
                        sensitivityModelData: null,
                    };
                });

                // Update parent's childrens
                if (newTopItem && newInputItems[parentItem.id]) {
                    newInputItems[parentItem.id] = {
                        ...newInputItems[parentItem.id],
                        childrens: [newTopItem.id.toString(), ...newInputItems[parentItem.id].childrens],
                        expanded: true,
                        anchorEl: null,
                    };
                }

                // Add new output items
                Object.entries(NewItems['output']).forEach(([newId, NewItem]: any) => {
                    newOutputItems[newId] = {
                        id: newId,
                        name: NewItem.name,
                        type: NewItem.type,
                        expanded: false,
                        position: NewItem.position,
                        kind: NewItem.kind,
                        has_related_output: NewItem.has_related_output,
                        relatedModelDataId: NewItem.related_model_id,
                        documentation: NewItem.documentation,
                        editing: false,
                        anchorEl: null,
                        deleteModalOpen: false,
                        detailsModalOpen: false,
                        childrens: NewItem.childrens.map(e => e.toString()),
                        possibleChildrens: NewItem.possible_childrens,
                        childrensDefinition: NewItem.childrens_definition,
                        parentId: NewItem.parent_id.toString(),
                        fields: FieldsInitializer(NewItem.fields),
                        isInconsiderable: NewItem.is_inconsiderable,
                        isDeletable: NewItem.is_deletable,
                        sensitivityModelData: null,
                    };
                });

                // Update output parent's childrens
                if (newTopItem?.has_related_output) {
                    const outputParentId = parentItem.relatedModelDataId?.toString();
                    if (outputParentId && newOutputItems[outputParentId]) {
                        newOutputItems[outputParentId] = {
                            ...newOutputItems[outputParentId],
                            childrens: [newTopItem.related_model_id.toString(), ...newOutputItems[outputParentId].childrens],
                        };
                    }
                }

                return {
                    ...prev,
                    isComputed: false,
                    isSync: false,
                    Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
                    Output: { ...prev.Output, tree: { ...prev.Output.tree, items: newOutputItems } },
                };
            });
        }

        useEditorStore.getState().setNodeLoading(nodeKey, false);
    }

    // Backend handles filtering of unique/mutual_exclusion/unique_child constraints in childrens_definition.
    // When rendered inside a section with an allowlist, hide creatable types that don't belong to that section
    // (e.g. don't propose "Add Equity" on a company while the user is in the Asset section).
    const rawPossibleChildrens = item.childrensDefinition || [];
    const currentPossibleChildrens = allowedTypes
        ? rawPossibleChildrens.filter((def: any) => allowedTypes.has((def?.type || '').toLowerCase()))
        : rawPossibleChildrens;

    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dropdownPos, setDropdownPos] = useState<React.CSSProperties>({});

    const handleButtonClick = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const estimatedHeight = currentPossibleChildrens.length * 40 + 8;

            if (spaceBelow >= estimatedHeight) {
                setDropdownPos({
                    position: 'fixed',
                    top: rect.bottom + 4,
                    left: rect.left,
                    zIndex: 50,
                });
            } else {
                setDropdownPos({
                    position: 'fixed',
                    bottom: window.innerHeight - rect.top + 4,
                    left: rect.left,
                    zIndex: 50,
                });
            }
        }
    };

    return (
        <>
        {(
            item.kind !== 'Output' &&
            currentPossibleChildrens.length &&
            level < 6 &&
            item.sensitivityModelData == null
        ) ?
        <Menu as="div" className="relative inline-block ml-1">
            {({ open }) => (
            <>
            <Menu.Button
                ref={buttonRef}
                id="add-elem-button"
                className="p-1 rounded text-inherit hover:bg-black/5 transition-colors"
                aria-haspopup="true"
                onClick={handleButtonClick}
            >
                <span className="material-symbols-outlined text-[20px]">add</span>
            </Menu.Button>

            <Transition
                show={!!open}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items
                    className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden"
                    style={dropdownPos}
                >
                    <div className="p-0">
                        {
                            currentPossibleChildrens.map((elem) => (
                                <Menu.Item key={elem.type}>
                                    {({ active }) => (
                                        <button
                                            title={elem.instantiable ? "" : "Upgrade your plan"}
                                            onClick={() => {
                                                if (elem.instantiable) {
                                                    addItemOnTree(item, elem, tree, scenario_id);
                                                }
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left transition-colors',
                                                !elem.instantiable && 'text-[#BBBBBB]'
                                            )}
                                            style={{
                                                backgroundColor: bgcolor,
                                                boxShadow: active ? 'inset 0 0 0 999px rgba(0,0,0,0.07)' : 'none',
                                            }}
                                        >
                                            {
                                                elem.instantiable ?
                                                <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">add</span> :
                                                <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">lock</span>
                                            }
                                            {snakeToPascal(elem.type)}

                                            <DocumentationButton elem={elem}/>
                                        </button>
                                    )}
                                </Menu.Item>
                            ))
                        }
                        {process.env.REACT_APP_LIBRARY_ENABLED === 'true' && (
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={() => setLibraryBrowserOpen(true)}
                                    className="flex items-center w-full px-3 py-2 text-sm text-left transition-colors border-t border-black/10"
                                    style={{
                                        backgroundColor: bgcolor,
                                        boxShadow: active ? 'inset 0 0 0 999px rgba(0,0,0,0.07)' : 'none',
                                    }}
                                >
                                    <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">library_add</span>
                                    From Library...
                                </button>
                            )}
                        </Menu.Item>
                        )}
                    </div>
                </Menu.Items>
            </Transition>
            </>
            )}
        </Menu> : <div></div>}
        <LibraryBrowserModal
            isOpen={libraryBrowserOpen}
            onClose={() => setLibraryBrowserOpen(false)}
            parentModelDataId={parseInt(item.id)}
            scenarioId={scenario_id}
            onApplied={handleLibraryApplied}
        />
        </>
    )
}
